import { useState, useEffect } from "react";
import axios from "axios";
import { Mail, Loader2, X, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Email Tax Prep modal — wraps the Tax Prep PDF (and optionally archive snapshots)
 * into an email and sends via Resend (POST /api/reports/email).
 *
 * Props:
 *   - open (boolean): controls visibility
 *   - onClose (fn): close handler
 *   - taxYear (number): the year being sent
 *   - centerName (string): displayed in the header for context
 *
 * Behavior:
 *   - On open, fetches recent recipients (saved_accountant_emails) for autocomplete pills
 *   - User adds 1–10 recipients, optionally writes a message, toggles "Save for next time"
 *   - "Send" calls /api/reports/email; toasts on success/error
 */
export default function EmailTaxPrepModal({ open, onClose, taxYear, centerName }) {
  const [recipients, setRecipients] = useState([]);
  const [recipientInput, setRecipientInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [message, setMessage] = useState("");
  const [saveRecipients, setSaveRecipients] = useState(true);
  const [recent, setRecent] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRecipients([]);
    setRecipientInput("");
    setCcInput("");
    setMessage("");
    setSaveRecipients(true);
    axios.get(`${API}/reports/email/recent`, { withCredentials: true })
      .then(({ data }) => setRecent(data?.recipients || []))
      .catch(() => setRecent([]));
  }, [open]);

  if (!open) return null;

  const addRecipient = (addr) => {
    const e = (addr || recipientInput || "").trim().toLowerCase();
    if (!e) return;
    if (recipients.includes(e)) {
      setRecipientInput("");
      return;
    }
    if (recipients.length >= 10) {
      toast.error("Maximum 10 recipients");
      return;
    }
    setRecipients([...recipients, e]);
    setRecipientInput("");
  };

  const removeRecipient = (addr) => {
    setRecipients(recipients.filter((r) => r !== addr));
  };

  const removeRecent = async (addr) => {
    try {
      await axios.delete(`${API}/reports/email/recent/${encodeURIComponent(addr)}`, { withCredentials: true });
      setRecent(recent.filter((r) => r.email !== addr));
    } catch {/* silent */ }
  };

  const send = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    setSending(true);
    try {
      const cc = ccInput.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
      const { data } = await axios.post(`${API}/reports/email`, {
        tax_year: taxYear,
        recipients,
        cc,
        message,
        save_recipients: saveRecipients,
      }, { withCredentials: true });
      if (data?.sent) {
        toast.success(`Email sent to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`);
      } else {
        toast.warning(`Email queued (${data?.reason || "preview mode"})`);
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const filteredRecent = recent.filter((r) => !recipients.includes(r.email));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="email-tax-prep-modal">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" /> Email Tax Prep — {taxYear}
            </h3>
            <p className="text-xs text-slate-500 mt-1">Send the Tax Prep PDF directly to your accountant.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="email-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Recipients</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                placeholder="accountant@firm.com"
                className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="email-recipient-input"
              />
              <button
                onClick={() => addRecipient()}
                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm inline-flex items-center gap-1"
                data-testid="email-add-recipient"
              >
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            {recipients.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2" data-testid="email-recipient-pills">
                {recipients.map((r) => (
                  <span key={r} className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-800 text-xs px-2 py-1 rounded-full">
                    {r}
                    <button onClick={() => removeRecipient(r)} className="hover:text-indigo-900" data-testid={`email-remove-${r}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {filteredRecent.length > 0 && (
              <div className="mt-2">
                <div className="text-xs text-slate-500 mb-1">Recent recipients:</div>
                <div className="flex flex-wrap gap-1">
                  {filteredRecent.slice(0, 5).map((r) => (
                    <button
                      key={r.email}
                      onClick={() => addRecipient(r.email)}
                      className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full inline-flex items-center gap-1 group"
                      title={`Last used: ${r.last_used_at ? new Date(r.last_used_at).toLocaleDateString() : ""}`}
                    >
                      <Plus className="w-3 h-3" /> {r.email}
                      <span
                        onClick={(e) => { e.stopPropagation(); removeRecent(r.email); }}
                        className="opacity-0 group-hover:opacity-100 ml-1 hover:text-red-600"
                        role="button"
                        title="Forget this address"
                      >
                        <Trash2 className="w-3 h-3" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* CC */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">CC (optional)</label>
            <input
              type="text"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value)}
              placeholder="bookkeeper@firm.com, partner@firm.com"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="email-cc-input"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Message (optional)</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={`Hi — attaching my ${taxYear} tax prep for your review. Let me know if you need anything else.`}
              maxLength={2000}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
              data-testid="email-message-input"
            />
            <div className="text-xs text-slate-400 text-right mt-1">{message.length}/2000</div>
          </div>

          {/* Save toggle */}
          <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-700">
            <input
              type="checkbox"
              checked={saveRecipients}
              onChange={(e) => setSaveRecipients(e.target.checked)}
              data-testid="email-save-recipients-toggle"
            />
            Save recipients for future emails
          </label>

          {/* Info banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 flex items-start gap-2">
            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>The Tax Prep PDF will be attached automatically. The email will be sent from {centerName || "your center"}'s name with your email as the reply-to.</div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg">Cancel</button>
          <button
            onClick={send}
            disabled={sending || recipients.length === 0}
            className="text-sm px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg inline-flex items-center gap-2"
            data-testid="email-send-btn"
          >
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Mail className="w-4 h-4" /> Send Email</>}
          </button>
        </div>
      </div>
    </div>
  );
}
